import { getErrorMessage } from '../../utils/serviceUtils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { GameEvent } from '../../types/game';
import { debugLog as globalDebugLog } from '../debug/debugLog';
import { dispatchApplyEvent } from '../debug/applyEventTrace';
import { isVerboseLoggingEnabled, logger } from '../../utils/logger';
import { normalizeEventForRpc } from '../../types/eventContracts';
import { GameAction } from '../gameReducer';
import { createOnlineTransport } from '../../services/transport/createOnlineTransport';
import { OnlineClientRole, OnlineServerMessage } from '../../types/transport';
import { registerOnlineTransportSession, unregisterOnlineTransportSession } from '../../services/transport/onlineTransportRuntime';
import type { OnlineSnapshot } from '../../types/snapshots';
import { getReservedPlayerId, getSessionPlayerId } from '../../utils/sessionPlayer';

interface UseOnlineEventsDeps {
  gameId: string;
  dispatch: React.Dispatch<GameAction>;
  currentClientPlayerId?: string | null;
  currentClientPlayerName?: string | null;
  currentClientRole?: OnlineClientRole | null;
  onPlayerReadyApplied?: (playerId: string) => void;
  onOnlineEventApplied?: (event: GameEvent) => void;
  onOnlineStatusChange?: (status: string) => void;
  onOnlineSnapshot?: (snapshot: OnlineSnapshot) => void;
  onOnlineServerError?: (message: Extract<OnlineServerMessage, { type: 'ERROR' }>) => void;
}

const SOURCE = 'useOnlineEvents';

function debugLog(label: string, payload: unknown) {
  if (isVerboseLoggingEnabled()) {
    try {
      const sanitized = typeof payload === 'object' && payload !== null
        ? JSON.parse(JSON.stringify(payload, (_, value) => (typeof value === 'string' && value.length > 32) ? `${value.slice(0, 8)}…${value.slice(-4)}` : value))
        : payload;
      logger.debug(`ONLINE_${label}`, sanitized);
    } catch {
      logger.debug(`ONLINE_${label}`, payload);
    }
  }
  globalDebugLog(`online:${label}`, payload, { source: SOURCE });
}

const shortId = (id?: string | null) => (id ? id.slice(0, 8) : null);

/**
 * Hook do synchronizacji eventów przez PartyKit.
 * Deduplikuje eventy po UUID (event_id) z klienta.
 */
export function useOnlineEvents({
  gameId,
  dispatch,
  currentClientPlayerId = null,
  currentClientPlayerName = null,
  currentClientRole = null,
  onPlayerReadyApplied,
  onOnlineEventApplied,
  onOnlineStatusChange,
  onOnlineSnapshot,
  onOnlineServerError,
}: UseOnlineEventsDeps) {
  const processedOnlineEvents = useRef<Set<string>>(new Set());
  const transport = useMemo(() => createOnlineTransport(), []);

  const applyIncomingEvent = useCallback((event: GameEvent) => {
    const gameEvent = normalizeEventForRpc(event);
    if (processedOnlineEvents.current.has(gameEvent.id)) {
      debugLog('dedupe:skip', { id: gameEvent.id });
      return;
    }

    processedOnlineEvents.current.add(gameEvent.id);
    if (processedOnlineEvents.current.size > 1000) {
      const keep = Array.from(processedOnlineEvents.current).slice(-500);
      processedOnlineEvents.current.clear();
      keep.forEach(id => processedOnlineEvents.current.add(id));
    }

    // Jeden wpis na zastosowane zdarzenie: `dispatchApplyEvent` niesie już opis rozliczenia,
    // więc osobny `online:event:apply` byłby tym samym wierszem raz jeszcze.
    dispatchApplyEvent(dispatch, gameEvent, 'online', {
      sequenceNumber: gameEvent.sequenceNumber ?? null,
      transport: 'PARTYKIT',
    });
    if (onOnlineEventApplied) {
      onOnlineEventApplied(gameEvent);
    }
    if (gameEvent.type === 'PLAYER_READY' && onPlayerReadyApplied) {
      onPlayerReadyApplied(gameEvent.payload.playerId);
    }
  }, [dispatch, onPlayerReadyApplied, onOnlineEventApplied]);

  useEffect(() => {
    if (!gameId) return;
    const sessionPlayerId = getSessionPlayerId();
    const reservedPlayerId = getReservedPlayerId(gameId);
    const effectivePlayerId = currentClientPlayerId || sessionPlayerId || reservedPlayerId;
    debugLog('subscribe:start', {
      gameId,
      transport: 'PARTYKIT',
      currentClientPlayerId: shortId(currentClientPlayerId),
      sessionPlayerId: shortId(sessionPlayerId),
      reservedPlayerId: shortId(reservedPlayerId),
      effectivePlayerId: shortId(effectivePlayerId),
    });
    let mounted = true;
    let disconnect: (() => Promise<void>) | null = null;
    let offMessage: (() => void) | null = null;
    let offStatus: (() => void) | null = null;

    void transport.connect(gameId, {
      playerId: effectivePlayerId,
      playerName: effectivePlayerId === currentClientPlayerId ? currentClientPlayerName : null,
      role: effectivePlayerId === currentClientPlayerId ? currentClientRole : null,
    }).then((session) => {
      if (!mounted) {
        void session.disconnect();
        return;
      }
      disconnect = () => session.disconnect();
      registerOnlineTransportSession(gameId, session);
      offMessage = session.onMessage((message: OnlineServerMessage) => {
        if (message.type === 'ERROR') {
          const telemetry = {
            gameId,
            transport: 'PARTYKIT' as const,
            code: message.code,
            message: message.message,
            commandType: message.commandType ?? null,
            eventId: shortId(message.eventId ?? null),
            turn: typeof message.turn === 'number' ? message.turn : null,
            activePlayerId: shortId(message.activePlayerId ?? null),
            serverTs: message.serverTs ?? null,
          };
          debugLog('server:error', telemetry);
          logger.warn('PARTYKIT_SERVER_ERROR', telemetry);
          if (onOnlineServerError) {
            onOnlineServerError(message);
          }
          return;
        }
        if (message.type === 'LOBBY_STATE') {
          dispatch({ type: 'HYDRATE_LOBBY_STATE', snapshot: message.state });
          return;
        }
        if (message.type === 'SNAPSHOT') {
          if ('scores' in message.snapshot && typeof message.snapshot.gameId === 'string') {
            if (onOnlineSnapshot) {
              onOnlineSnapshot(message.snapshot);
            } else {
              dispatch({ type: 'HYDRATE_FROM_SNAPSHOT', snapshot: message.snapshot });
            }
          }
          return;
        }
        if (message.type !== 'EVENT_APPLIED') return;
        applyIncomingEvent(message.event);
      });
      offStatus = session.onStatus((status: string) => {
        debugLog('subscribe:status', { status, gameId, transport: 'PARTYKIT' });
        if (onOnlineStatusChange) {
          onOnlineStatusChange(status);
        }
      });
      void session.send({ type: 'REQUEST_STATE' });
    }).catch((error: unknown) => {
      debugLog('subscribe:error', {
        gameId,
        transport: 'PARTYKIT',
        error: getErrorMessage(error),
      });
    });

    return () => {
      mounted = false;
      if (offMessage) offMessage();
      if (offStatus) offStatus();
      unregisterOnlineTransportSession(gameId);
      if (disconnect) {
        void disconnect();
      }
    };
  }, [
    applyIncomingEvent,
    currentClientPlayerId,
    currentClientPlayerName,
    currentClientRole,
    dispatch,
    gameId,
    onOnlineStatusChange,
    onOnlineServerError,
    onOnlineSnapshot,
    transport,
  ]);
}

